import axios from "axios";
import { db } from "../../firebase";
import {
  collection, doc, getDocs, getDoc, updateDoc, deleteDoc, query, where, orderBy, serverTimestamp, setDoc, limit as firestoreLimit, startAfter, runTransaction
} from "firebase/firestore";
import Swal from 'sweetalert2';
import { getSpecialtyCollection, getCounterDocPath, getActiveSpecialty } from "util/specialtyCollections";
import {
  SAVE_TEST_PROGRESS_REQUEST,
  SAVE_TEST_PROGRESS_SUCCESS,
  SAVE_TEST_PROGRESS_FAILURE,
  FETCH_TESTS_REQUEST,
  FETCH_TESTS_SUCCESS,
  FETCH_TESTS_FAILURE,
  FETCH_TEST_REQUEST,
  FETCH_TEST_SUCCESS,
  FETCH_TEST_FAILURE,
  DELETE_TEST_REQUEST,
  DELETE_TEST_SUCCESS,
  DELETE_TEST_FAILURE,
  UPDATE_TEST_REQUEST,
  UPDATE_TEST_SUCCESS,
  UPDATE_TEST_FAILURE,
  FETCH_USER_ANSWER_HISTORY_REQUEST,
  FETCH_USER_ANSWER_HISTORY_SUCCESS,
  FETCH_USER_ANSWER_HISTORY_FAILURE,
  FETCH_MISSED_QUESTIONS_REQUEST,
  FETCH_MISSED_QUESTIONS_SUCCESS,
  FETCH_MISSED_QUESTIONS_FAILURE,
  FETCH_USER_PERFORMANCE_REQUEST,
  FETCH_USER_PERFORMANCE_SUCCESS,
  FETCH_USER_PERFORMANCE_FAILURE,
  FETCH_PEER_DISTRIBUTION_REQUEST,
  FETCH_PEER_DISTRIBUTION_SUCCESS,
  FETCH_PEER_DISTRIBUTION_FAILURE,
  CLEAR_CURRENT_TEST
} from './TestProgressTypes';

const AUDIT_SUCCESS_SAMPLE_RATE = 0.01; // 1% sampled success logs
const AUDIT_ALWAYS_LOG_EVENTS = new Set([
  "save_failure",
  "update_failure",
  "save_owner_collision_reassigned",
  "save_blocked_owner_mismatch",
  "update_blocked_owner_mismatch"
]);

const shouldWriteAuditLog = (eventType) => {
  if (!eventType) return true;
  if (AUDIT_ALWAYS_LOG_EVENTS.has(eventType)) return true;
  if (eventType.endsWith("_success")) {
    return Math.random() < AUDIT_SUCCESS_SAMPLE_RATE;
  }
  return true;
};

const writeTestProgressAuditLog = async (payload, getState) => {
  try {
    const eventType = payload?.eventType || null;
    if (!shouldWriteAuditLog(eventType)) {
      return;
    }

    const auditRef = collection(db, getSpecialtyCollection("testProgressAudit", getState));
    const auditDocRef = doc(auditRef);
    await setDoc(auditDocRef, {
      auditId: auditDocRef.id,
      ...payload,
      ...(eventType?.endsWith("_success") && { sampledSuccess: true }),
      loggedAt: serverTimestamp()
    });
  } catch (auditError) {
    console.error("Failed to write test progress audit log:", auditError);
  }
};

const formatTestIdFromNumber = (testNumber) => `TEST-${String(testNumber).padStart(9, '0')}`;

const reserveNextAvailableTestIdentity = async (testsRef, getState, maxAttempts = 10) => {
  const counterRef = doc(db, "counters", getCounterDocPath(getState));

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    const nextNumber = await runTransaction(db, async (transaction) => {
      const counterDoc = await transaction.get(counterRef);
      const nextCount = (counterDoc.exists() ? (counterDoc.data().count || 0) : 0) + 1;
      transaction.set(counterRef, { count: nextCount }, { merge: true });
      return nextCount;
    });

    const nextTestId = formatTestIdFromNumber(nextNumber);
    const nextDocRef = doc(testsRef, nextTestId);
    const nextDoc = await getDoc(nextDocRef);

    if (!nextDoc.exists()) {
      return { testId: nextTestId, testNumber: nextNumber };
    }
  }

  throw new Error("Unable to allocate a unique test ID. Please try again.");
};

// Save or update test progress
export const saveTestProgress = (testData) => {
  return async (dispatch, getState) => {
    const writeId = doc(collection(db, getSpecialtyCollection("testProgressAudit", getState))).id;
    let resolvedTestId = testData?.testId || null;
    let resolvedTestNumber = testData?.testNumber || null;
    let existingOwner = null;
    let writeMode = "create";

    try {
      dispatch({ type: SAVE_TEST_PROGRESS_REQUEST });

      const userHandle = getState().loginAuth?.userDetails?.handle;
      if (!userHandle) {
        throw new Error('User not authenticated');
      }

      const testsRef = collection(db, getSpecialtyCollection("testProgress", getState));

      // Check if this test already exists and has a testNumber
      let testNumber = testData.testNumber;
      let testId = testData.testId;

      if (!testNumber) {
        // Check if this test was previously saved (has a testNumber in DB)
        const existingTestRef = testData.testId ? doc(testsRef, testData.testId) : null;
        const existingTestDoc = existingTestRef ? await getDoc(existingTestRef) : null;
        const existingTestData = existingTestDoc?.exists() ? existingTestDoc.data() : null;

        if (
          existingTestData?.testNumber &&
          existingTestData?.userHandle === userHandle
        ) {
          // Use existing test number for this user's own test.
          testNumber = existingTestData.testNumber;
        } else {
          const nextIdentity = await reserveNextAvailableTestIdentity(testsRef, getState);
          testNumber = nextIdentity.testNumber;
          testId = nextIdentity.testId;
        }
      }

      resolvedTestId = testId;
      resolvedTestNumber = testNumber;

      let collisionReassignCount = 0;
      let testDocRef = doc(testsRef, testId);
      let targetDocBeforeWrite = await getDoc(testDocRef);

      while (targetDocBeforeWrite.exists()) {
        writeMode = "update";
        existingOwner = targetDocBeforeWrite.data()?.userHandle || null;

        if (!existingOwner || existingOwner === userHandle) {
          break;
        }

        const previousTestId = testId;
        const previousTestNumber = testNumber;
        const previousOwner = existingOwner;
        const nextIdentity = await reserveNextAvailableTestIdentity(testsRef, getState);

        testId = nextIdentity.testId;
        testNumber = nextIdentity.testNumber;
        resolvedTestId = testId;
        resolvedTestNumber = testNumber;
        writeMode = "create_reassigned";
        collisionReassignCount += 1;
        existingOwner = null;

        await writeTestProgressAuditLog({
          writeId,
          eventType: "save_owner_collision_reassigned",
          testId,
          testNumber,
          userHandle,
          status: testData.status || null,
          previousTestId,
          previousTestNumber,
          previousOwner,
          collisionReassignCount,
          source: "saveTestProgress"
        }, getState);

        testDocRef = doc(testsRef, testId);
        targetDocBeforeWrite = await getDoc(testDocRef);
      }

      const testDocument = {
        testId: testId,
        testNumber: testNumber,
        testName: testData.testName,
        status: testData.status,
        testQuestions: testData.testQuestions,
        currentQuestionIndex: testData.currentQuestionIndex,
        selectedAnswers: testData.selectedAnswers,
        markedQuestions: testData.markedQuestions,
        submittedQuestions: testData.submittedQuestions || [],
        annotations: testData.annotations,
        timeSpent: testData.timeSpent,
        timeRemaining: testData.timeRemaining,
        elapsedTime: testData.elapsedTime || 0, // For untimed tests - time spent counting up
        isTimed: testData.isTimed,
        isTutor: testData.isTutor,
        selectionMode: testData.selectionMode,
        timeAccommodation: testData.timeAccommodation || 1.0,
        questionTimeTracking: testData.questionTimeTracking || {},
        totalQuestions: testData.totalQuestions,
        subjects: testData.subjects,
        units: testData.units,
        userHandle: userHandle,
        createdAt: testData.createdAt || serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...(testData.status === 'completed' && {
          score: testData.score,
          correctAnswers: testData.correctAnswers,
          completedAt: serverTimestamp()
        })
      };

      // Use setDoc to create or update
      await setDoc(testDocRef, testDocument, { merge: true });

      await writeTestProgressAuditLog({
        writeId,
        eventType: "save_success",
        testId,
        testNumber,
        userHandle,
        existingOwner,
        writeMode,
        collisionReassignCount,
        status: testData.status || null,
        totalQuestions: testData.totalQuestions || testData.testQuestions?.length || 0,
        selectedAnswersCount: Object.keys(testData.selectedAnswers || {}).length,
        submittedQuestionsCount: (testData.submittedQuestions || []).length,
        markedQuestionsCount: (testData.markedQuestions || []).length,
        source: "saveTestProgress"
      }, getState);

      const savedTest = {
        ...testDocument,
        testId: testId,
        testNumber: testNumber,
        createdAt: testData.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        ...(testData.status === 'completed' && {
          completedAt: new Date().toISOString()
        })
      };

      dispatch({
        type: SAVE_TEST_PROGRESS_SUCCESS,
        payload: savedTest
      });

      // C2 fix: removed duplicate Swal here — the completion dialog is shown
      // by handleSubmitTest in CreateTest.jsx to avoid two overlapping popups

      return { success: true, testId: testId };
    } catch (error) {
      dispatch({
        type: SAVE_TEST_PROGRESS_FAILURE,
        payload: error.message
      });

      await writeTestProgressAuditLog({
        writeId,
        eventType: "save_failure",
        testId: resolvedTestId,
        testNumber: resolvedTestNumber,
        userHandle: getState().loginAuth?.userDetails?.handle || null,
        existingOwner,
        writeMode,
        status: testData?.status || null,
        errorMessage: error.message || "Unknown save error",
        source: "saveTestProgress"
      }, getState);

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to save test progress',
        confirmButtonColor: '#5e72e4'
      });

      return { success: false, error: error.message };
    }
  };
};

// Fetch tests for current user — uses lightweight backend endpoint (excludes heavy arrays)
export const fetchTests = ({ pageSize = 100, lastCreatedAt = null, append = false } = {}) => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: FETCH_TESTS_REQUEST });

      const userHandle = getState().loginAuth?.userDetails?.handle;
      const specialty = getActiveSpecialty(getState);
      const res = await axios.post("/fetchTestsList", { pageSize, lastCreatedAt, userHandle, specialty });
      const { tests, hasMore } = res.data;

      // If appending (load more), merge with existing tests
      if (append) {
        const existingTests = getState().testProgress.tests || [];
        dispatch({
          type: FETCH_TESTS_SUCCESS,
          payload: [...existingTests, ...tests]
        });
      } else {
        dispatch({
          type: FETCH_TESTS_SUCCESS,
          payload: tests
        });
      }

      // Return lastCreatedAt for cursor-based pagination
      const lastTest = tests[tests.length - 1];
      const nextCursor = lastTest?.createdAt || null;
      return { success: true, tests, lastCreatedAt: nextCursor, hasMore };
    } catch (error) {
      dispatch({
        type: FETCH_TESTS_FAILURE,
        payload: error.response?.data?.error || error.message
      });

      return { success: false, error: error.message };
    }
  };
};

// Fetch tests with full data (includes testQuestions, selectedAnswers, questionTimeTracking)
// Used by Performance page which needs question-level analytics
export const fetchTestsFull = () => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: FETCH_TESTS_REQUEST });

      const userHandle = getState().loginAuth?.userDetails?.handle;
      if (!userHandle) {
        throw new Error('User not authenticated');
      }

      const testsRef = collection(db, getSpecialtyCollection("testProgress", getState));
      const constraints = [
        where("userHandle", "==", userHandle),
        orderBy("createdAt", "desc")
      ];

      const q = query(testsRef, ...constraints);
      const snapshot = await getDocs(q);

      const tests = snapshot.docs.map(docSnap => ({
        testId: docSnap.id,
        ...docSnap.data(),
        createdAt: docSnap.data().createdAt?.toMillis?.() || docSnap.data().createdAt,
        updatedAt: docSnap.data().updatedAt?.toMillis?.() || docSnap.data().updatedAt,
        completedAt: docSnap.data().completedAt?.toMillis?.() || docSnap.data().completedAt
      }));

      dispatch({
        type: FETCH_TESTS_SUCCESS,
        payload: tests
      });

      return { success: true, tests };
    } catch (error) {
      dispatch({
        type: FETCH_TESTS_FAILURE,
        payload: error.message
      });

      return { success: false, error: error.message };
    }
  };
};

// Fetch single test by ID
export const fetchTest = (testId) => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: FETCH_TEST_REQUEST });

      const userHandle = getState().loginAuth?.userDetails?.handle;
      if (!userHandle) {
        throw new Error('User not authenticated');
      }

      const testRef = doc(db, getSpecialtyCollection("testProgress", getState), testId);
      const testDoc = await getDoc(testRef);

      if (testDoc.exists()) {
        const testData = testDoc.data();

        // Verify ownership
        if (testData.userHandle !== userHandle) {
          throw new Error('Unauthorized access to test');
        }

        const test = {
          testId: testDoc.id,
          ...testData,
          createdAt: testData.createdAt?.toMillis?.() || testData.createdAt,
          updatedAt: testData.updatedAt?.toMillis?.() || testData.updatedAt,
          completedAt: testData.completedAt?.toMillis?.() || testData.completedAt
        };

        dispatch({
          type: FETCH_TEST_SUCCESS,
          payload: test
        });

        return { success: true, test };
      } else {
        throw new Error("Test not found");
      }
    } catch (error) {
      dispatch({
        type: FETCH_TEST_FAILURE,
        payload: error.message
      });

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to load test',
        confirmButtonColor: '#5e72e4'
      });

      return { success: false, error: error.message };
    }
  };
};

// Delete test
export const deleteTest = (testId) => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: DELETE_TEST_REQUEST });

      const userHandle = getState().loginAuth?.userDetails?.handle;
      if (!userHandle) {
        throw new Error('User not authenticated');
      }

      // Verify ownership before deleting
      const testRef = doc(db, getSpecialtyCollection("testProgress", getState), testId);
      const testDoc = await getDoc(testRef);

      if (testDoc.exists()) {
        const testData = testDoc.data();
        if (testData.userHandle !== userHandle) {
          throw new Error('Unauthorized to delete this test');
        }
      }

      await deleteDoc(testRef);

      dispatch({
        type: DELETE_TEST_SUCCESS,
        payload: testId
      });

      Swal.fire({
        icon: 'success',
        title: 'Deleted!',
        text: 'Test has been deleted successfully',
        confirmButtonColor: '#5e72e4',
        timer: 2000
      });

      return { success: true };
    } catch (error) {
      dispatch({
        type: DELETE_TEST_FAILURE,
        payload: error.message
      });

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to delete test',
        confirmButtonColor: '#5e72e4'
      });

      return { success: false, error: error.message };
    }
  };
};

// Fetch user answer history - aggregates answers from all completed tests via backend
// Returns: { [questionId]: { isCorrect, isMarked, wasOmitted, lastAnsweredAt } }
export const fetchUserAnswerHistory = () => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: FETCH_USER_ANSWER_HISTORY_REQUEST });

      const userHandle = getState().loginAuth?.userDetails?.handle;
      const res = await axios.post("/fetchUserAnswerHistory", { userHandle, specialty: getActiveSpecialty(getState) });
      const { answerHistory } = res.data;

      dispatch({
        type: FETCH_USER_ANSWER_HISTORY_SUCCESS,
        payload: answerHistory
      });

      return { success: true, answerHistory };
    } catch (error) {
      dispatch({
        type: FETCH_USER_ANSWER_HISTORY_FAILURE,
        payload: error.response?.data?.error || error.message
      });

      return { success: false, error: error.message };
    }
  };
};

// Fetch every question the user has ever answered incorrectly or omitted.
// Unlike fetchUserAnswerHistory (latest answer only), this accumulates across
// all completed tests, so questions since answered correctly still appear.
export const fetchMissedQuestions = () => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: FETCH_MISSED_QUESTIONS_REQUEST });

      const userHandle = getState().loginAuth?.userDetails?.handle;
      const specialty = getActiveSpecialty(getState);
      const res = await axios.post("/fetchMissedQuestions", { userHandle, specialty });
      const { missedQuestions } = res.data;

      // Record which specialty this data belongs to so consumers can tell
      // when the cached result is for a different one
      dispatch({
        type: FETCH_MISSED_QUESTIONS_SUCCESS,
        payload: { missedQuestions: missedQuestions || {}, specialty }
      });

      return { success: true, missedQuestions };
    } catch (error) {
      dispatch({
        type: FETCH_MISSED_QUESTIONS_FAILURE,
        payload: error.response?.data?.error || error.message
      });

      return { success: false, error: error.message };
    }
  };
};

// Fetch user performance data from pre-computed backend cache
// Returns: { overallStats, subjectPerformance, unitPerformance, topicPerformance, testSummaries }
export const fetchUserPerformance = () => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: FETCH_USER_PERFORMANCE_REQUEST });

      const userHandle = getState().loginAuth?.userDetails?.handle;
      const res = await axios.post("/fetchUserPerformance", { userHandle, specialty: getActiveSpecialty(getState) });
      const { overallStats, subjectPerformance, unitPerformance, topicPerformance, testSummaries } = res.data;

      dispatch({
        type: FETCH_USER_PERFORMANCE_SUCCESS,
        payload: { overallStats, subjectPerformance, unitPerformance, topicPerformance, testSummaries }
      });

      return { success: true };
    } catch (error) {
      dispatch({
        type: FETCH_USER_PERFORMANCE_FAILURE,
        payload: error.response?.data?.error || error.message
      });

      return { success: false, error: error.message };
    }
  };
};

// Fetch peer score distribution for real percentile ranking
export const fetchPeerDistribution = () => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: FETCH_PEER_DISTRIBUTION_REQUEST });

      const res = await axios.post("/fetchPeerDistribution", { specialty: getActiveSpecialty(getState) });

      dispatch({
        type: FETCH_PEER_DISTRIBUTION_SUCCESS,
        payload: res.data
      });

      return { success: true };
    } catch (error) {
      dispatch({
        type: FETCH_PEER_DISTRIBUTION_FAILURE,
        payload: error.response?.data?.error || error.message
      });

      return { success: false, error: error.message };
    }
  };
};

// Clear current test from Redux state (prevents stale data when switching tests)
export const clearCurrentTest = () => {
  return { type: CLEAR_CURRENT_TEST };
};

// Update test (for completing, updating score, etc.)
export const updateTest = (testId, updates) => {
  return async (dispatch, getState) => {
    const writeId = doc(collection(db, getSpecialtyCollection("testProgressAudit", getState))).id;
    let existingOwner = null;

    try {
      dispatch({ type: UPDATE_TEST_REQUEST });

      const userHandle = getState().loginAuth?.userDetails?.handle;
      if (!userHandle) {
        throw new Error('User not authenticated');
      }

      const testRef = doc(db, getSpecialtyCollection("testProgress", getState), testId);

      // Verify ownership
      const testDoc = await getDoc(testRef);
      if (testDoc.exists()) {
        const testData = testDoc.data();
        existingOwner = testData.userHandle || null;
        if (testData.userHandle !== userHandle) {
          await writeTestProgressAuditLog({
            writeId,
            eventType: "update_blocked_owner_mismatch",
            testId,
            userHandle,
            existingOwner,
            updates: Object.keys(updates || {}),
            source: "updateTest"
          }, getState);
          throw new Error('Unauthorized to update this test');
        }
      }

      const updateData = {
        ...updates,
        updatedAt: serverTimestamp()
      };

      await updateDoc(testRef, updateData);

      await writeTestProgressAuditLog({
        writeId,
        eventType: "update_success",
        testId,
        userHandle,
        existingOwner: existingOwner || userHandle,
        updates: Object.keys(updates || {}),
        source: "updateTest"
      }, getState);

      const updatedTest = {
        testId,
        ...updates,
        updatedAt: new Date().toISOString()
      };

      dispatch({
        type: UPDATE_TEST_SUCCESS,
        payload: updatedTest
      });

      return { success: true };
    } catch (error) {
      dispatch({
        type: UPDATE_TEST_FAILURE,
        payload: error.message
      });

      await writeTestProgressAuditLog({
        writeId,
        eventType: "update_failure",
        testId,
        userHandle: getState().loginAuth?.userDetails?.handle || null,
        existingOwner,
        updates: Object.keys(updates || {}),
        errorMessage: error.message || "Unknown update error",
        source: "updateTest"
      }, getState);

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to update test',
        confirmButtonColor: '#5e72e4'
      });

      return { success: false, error: error.message };
    }
  };
};
